import angular from 'angular'

import MapSimple from '../utils/map-simple'
import extractRawElm from '../utils/extract-raw-elm'

import { toObject } from '../utils/bits'
import { get, isString, uniqueId } from 'lodash-es'
import { offEvent, onEventPassive } from '../utils/on-event'

angular
  .module('ggs.frame')
  .factory('FrameLoaded', FrameLoadedProvider)

const SKIP_APPLY = false
const INFINITY_REPEAT = 0
const INTERVAL_CHECK_DELAY = 1e3
const DEFAULT_TIMEOUT_DELAY = 10e3

function FrameLoadedProvider($q, $timeout, $interval, frameHost) {
  const cache = MapSimple()
  return FrameLoaded

  function FrameLoaded($iframeElm, timeoutDelay = DEFAULT_TIMEOUT_DELAY) {
    const iframeElm = extractRawElm($iframeElm)
    const iframeSrc = iframeElm.src

    if (cache.has(iframeElm)) {
      return cache.get(iframeElm)
    }

    let waits = true
    const deferred = $q.defer()
    onEventPassive(iframeElm, 'load', trigger)
    onEventPassive(window, 'message', onMessage)
    const timeoutId = $timeout(onTimeout, timeoutDelay, SKIP_APPLY)
    const intervalId = $interval(trigger, INTERVAL_CHECK_DELAY, INFINITY_REPEAT, SKIP_APPLY)
    deferred.promise.kill = kill

    trigger()

    return cache.set(iframeElm, deferred.promise)

    function clear() {
      offEvent(iframeElm, 'load', trigger)
      offEvent(window, 'message', onMessage)
      $timeout.cancel(timeoutId)
      $interval.cancel(intervalId)
      cache.del(iframeElm)
    }

    function kill() {
      if (waits) {
        waits = false
        clear()
      }
    }

    function resolve() {
      if (waits) {
        waits = false
        clear()
        deferred.resolve($iframeElm)
      }
    }

    function reject(error) {
      if (waits) {
        waits = false
        clear()
        deferred.reject(error)
      }
    }

    function trigger() {
      if (!isFrameLoaded()) {
        return
      }

      if (frameHost.isVine(iframeSrc)) {
        triggerVineFrame()
      } else if (frameHost.isVimeo(iframeSrc)) {
        triggerVimeoFrame()
      } else if (frameHost.isGleam(iframeSrc)) {
        triggerGleamFrame()
      } else if (frameHost.isYoutube(iframeSrc)) {
        triggerYoutubeFrame()
      } else if (frameHost.isSoundcloud(iframeSrc)) {
        triggerSoundcloudFrame()
      }
    }

    function onTimeout() {
      reject(new Error(`FrameLoaded: load event did not fire in ${timeoutDelay}ms!`))
    }

    function onMessage(event) {
      if (event.source !== iframeElm.contentWindow) {
        return
      }

      const data = toObject(event.data)
      const vine = frameHost.isVine(iframeSrc) && /^(ping|loaded):/.test(data)
      const vimeo = frameHost.isVimeo(iframeSrc) && ((get(data, 'event') === 'ready') || (get(data, 'method') === 'ping'))
      const gleam = frameHost.isGleam(iframeSrc) && ('pong' === data)
      const youtube = frameHost.isYoutube(iframeSrc) && (get(data, 'event') === 'onReady')
      const soundcloud = frameHost.isSoundcloud(iframeSrc) && ((get(data, 'method') === 'ready') || (get(data, 'method') === 'getVolume'))

      if (vine || vimeo || gleam || youtube || soundcloud) {
        resolve()
      }
    }

    function isFrameLoaded() {
      return iframeElm.contentWindow && iframeElm.contentWindow.postMessage
    }

    function triggerVineFrame() {
      postMessage('pong')
    }

    function triggerVimeoFrame() {
      postMessage({ method: 'ping' })
    }

    function triggerGleamFrame() {
      postMessage('ping')
    }

    function triggerYoutubeFrame() {
      postMessage({ id: uniqueId('FrameLoaded'), event: 'listening' })
    }

    function triggerSoundcloudFrame() {
      postMessage({ method: 'getVolume' })
    }

    function postMessage(msg) {
      const str = isString(msg) ? msg : JSON.stringify(msg)
      iframeElm.contentWindow.postMessage(str, '*')
    }
  }
}
